<template>
  <nav class="flex items-center justify-between px-4 py-2 border-b border-gray-200 dark:border-gray-300 bg-white dark:bg-gray-50 flex-shrink-0">
    <div class="flex items-center gap-3">
      <!-- Projects sidebar toggle (desktop + mobile) -->
      <button
        @click="$emit('toggle-sidebar')"
        class="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-200"
        :class="sidebarOpen ? 'bg-teal-50 text-teal-600 dark:bg-teal-900/40 dark:text-teal-400' : 'text-gray-500 hover:text-gray-700'"
        :title="sidebarOpen ? 'Hide projects' : 'Show projects'"
        :aria-label="sidebarOpen ? 'Hide projects sidebar' : 'Show projects sidebar'"
        :aria-pressed="sidebarOpen"
      >
        <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16"/>
        </svg>
      </button>
      <span class="text-sm font-semibold text-teal-600">TaskMagic</span>
    </div>

    <div class="flex items-center gap-1">
      <button
        @click="$emit('refresh-data')"
        class="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-200 text-gray-400 hover:text-gray-600"
        :class="{ 'animate-spin': isRefreshing }"
        :disabled="isRefreshing"
        title="Refresh data"
      >
        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/>
        </svg>
      </button>

      <button
        v-if="updateAvailable"
        @click="$emit('apply-update')"
        class="px-2 py-1 rounded bg-teal-600 text-white text-xs font-medium hover:bg-teal-700"
        title="Load the newest app version"
      >
        Update
      </button>

      <!-- Back to work -->
      <button
        v-if="lastFocusTaskId"
        @click="$emit('back-to-work')"
        class="p-1.5 rounded hover:bg-blue-50 text-blue-500"
        :title="'Back to: ' + (lastFocusTaskTitle || '')"
      >
        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1"/>
        </svg>
      </button>

      <!-- Archive completed -->
      <button
        @click="$emit('archive-completed')"
        class="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-200 text-gray-400 hover:text-gray-600"
        title="Archive all completed tasks"
      >
        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4"/>
        </svg>
      </button>

      <!-- Settings -->
      <a href="/api_token" class="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-200 text-gray-400 hover:text-gray-600" title="Settings / API Token">
        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"/>
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/>
        </svg>
      </a>

      <!-- Organize mode toggle -->
      <button
        @click="$emit('toggle-organize')"
        class="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-200"
        :class="organizeMode
          ? 'bg-teal-50 text-teal-600 dark:bg-teal-900/40 dark:text-teal-400'
          : 'text-gray-400 hover:text-gray-600'"
        :title="organizeMode ? 'Exit organize mode' : 'Organize tasks'"
        :aria-pressed="organizeMode"
      >
        <!-- List with checkmarks — organize / multi-edit -->
        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2"/>
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"/>
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6M9 16h6"/>
        </svg>
      </button>

      <!-- Theme toggle -->
      <button @click="toggleTheme" class="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-200 text-gray-400 hover:text-gray-600" title="Toggle theme">
        <svg class="w-4 h-4 dark:hidden" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z"/>
        </svg>
        <svg class="w-4 h-4 hidden dark:block" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z"/>
        </svg>
      </button>
    </div>
  </nav>
</template>

<script setup>
defineProps({
  sidebarOpen: Boolean,
  isRefreshing: Boolean,
  updateAvailable: Boolean,
  isTracking: Boolean,
  elapsed: Number,
  formatDuration: Function,
  lastFocusTaskId: [Number, null],
  lastFocusTaskTitle: String,
  organizeMode: { type: Boolean, default: false },
})

defineEmits(['toggle-sidebar', 'stop-tracking', 'back-to-work', 'archive-completed', 'refresh-data', 'apply-update', 'toggle-organize'])

function toggleTheme() {
  const html = document.documentElement
  const isDark = html.classList.toggle('dark')
  localStorage.setItem('theme', isDark ? 'dark' : 'light')
}
</script>
